import React, { useState } from "react";
import { Box, Typography, Button, Container, Grid, Card, Avatar } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

// Testimonials mock
const testimonials = [
  { textKey: "testimonial_1", authorKey: "testimonial_author_1", avatar: "/avatars/Doukissa.jpg" },
  { textKey: "testimonial_2", authorKey: "testimonial_author_2", avatar: "/avatars/Vasilis.jpg" },
  { textKey: "testimonial_3", authorKey: "testimonial_author_3", avatar: "/avatars/Rita.jpg" }
];

const MAX_PREVIEW_CHARS = 140; 

const Home = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const makePath = (path) => `/${lang || "en"}${path}`;

  const [expanded, setExpanded] = useState({});

  const handleExpand = (idx) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <>
      <Helmet>
        <title>{t("meta_title_home")}</title>
        <meta name="description" content={t("meta_description")} />
        <link rel="alternate" hrefLang="en" href="brainstorm-visions.com/en" />
        <link rel="alternate" hrefLang="el" href="brainstorm-visions.com/el" />
      </Helmet>
      <Box sx={{ background: "linear-gradient(135deg, #1e293b 0%, #57a1d8 100%)", color: "#fff", minHeight: "100vh", pb: 8 }}>
        {/* Hero Section */}
        <Container sx={{ pt: { xs: 7, md: 13 }, pb: { xs: 4, md: 8 } }}>
          <motion.div initial={{ opacity: 0, y: -40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography
              variant="h2"
              fontWeight="bold"
              gutterBottom
              sx={{ fontSize: { xs: "2.1rem", sm: "2.8rem", md: "3.5rem" } }}
            >
              {t("welcome")}
            </Typography>
            <Typography variant="h5" mb={3} sx={{ fontSize: { xs: "1.07rem", md: "1.55rem" } }}>
              {t("slogan")}
            </Typography>
            <Button variant="contained" color="secondary" size="large" component={Link} to={makePath("/contact")} sx={{ mr: 2, mb: { xs: 2, sm: 0 } }}>
              {t("contact_page")}
            </Button>
            <Button variant="outlined" color="secondary" size="large" component={Link} to={makePath("/about")}>
              {t("learn_more")}
            </Button>
          </motion.div>
        </Container>

        {/* Why Us Section */}
        <Container>
          <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              mb={3}
              sx={{ fontSize: { xs: "1.5rem", md: "2.2rem" } }}
            >
              {t("why_us")}
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {[{
                icon: <PeopleAltIcon fontSize="large" />,
                title: t("human_centric"),
                desc: t("human_centric_desc")
              },
              {
                icon: <EmojiObjectsIcon fontSize="large" />,
                title: t("innovation"),
                desc: t("innovation_desc")
              },
              {
                icon: <FavoriteIcon fontSize="large" />,
                title: t("unique_experience"),
                desc: t("unique_experience_desc")
              },
              {
                icon: <MonetizationOnIcon fontSize="large" />,
                title: t("affordable"),
                desc: t("affordable_desc")
              }].map((item, idx) => (
                <Grid item xs={12} sm={6} md={3} key={idx}>
                  <motion.div whileHover={{ scale: 1.07, boxShadow: "0 4px 32px rgba(30,41,59,0.3)" }} transition={{ type: "spring", stiffness: 250 }}>
                    <Card sx={{
                      background: "rgba(30,41,59,0.88)",
                      color: "#fff",
                      textAlign: "center",
                      py: 3,
                      borderRadius: 3,
                      minHeight: { xs: 180, md: 210 }
                    }}>
                      {item.icon}
                      <Typography variant="h6" fontWeight="bold" mt={1}>{item.title}</Typography>
                      <Typography variant="body2">{item.desc}</Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>

        {/* Testimonials */}
        <Container sx={{ mt: 10, mb: 6 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              align="center"
              mb={4}
              sx={{ fontSize: { xs: "1.22rem", md: "1.8rem" } }}
            >
              {t("testimonials")}
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {testimonials.map((item, idx) => {
                const fullText = t(item.textKey);
                const previewText = fullText.slice(0, MAX_PREVIEW_CHARS);
                const isLong = fullText.length > MAX_PREVIEW_CHARS;
                const isExpanded = expanded[idx] || false;

                return (
                  <Grid item xs={12} md={4} key={idx}>
                    <motion.div
                      whileHover={{
                        scale: 1.04,
                        boxShadow: "0 6px 32px rgba(30,41,59,0.25)",
                      }}
                      transition={{ type: "spring", stiffness: 250 }}
                    >
                      <Card
                        sx={{
                          background: "rgba(255,255,255,0.93)",
                          color: "#1e293b",
                          p: 4,
                          textAlign: "center",
                          borderRadius: 3,
                          minHeight: { xs: 320, md: 370 },
                          maxHeight: { xs: 370, md: 420 },
                          boxShadow: 6,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Avatar
                          src={item.avatar}
                          alt={t(item.authorKey)}
                          sx={{
                            width: 70,
                            height: 70,
                            mb: 2,
                            border: "2px solid #57a1d8",
                            boxShadow: 2,
                          }}
                        />
                        {/* Εικόνες του χρήστη αν υπάρχουν */}
                        {item.photos && item.photos.length > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              mb: 2,
                              flexWrap: "wrap",
                              justifyContent: "center",
                            }}
                          >
                            {item.photos.map((photoUrl, i) => (
                              <Box
                                component="img"
                                key={i}
                                src={photoUrl}
                                alt={`testimonial-photo-${i}`}
                                sx={{
                                  width: 60,
                                  height: 60,
                                  objectFit: "cover",
                                  borderRadius: 2,
                                  boxShadow: 1,
                                  border: "1.5px solid #f0f0f0",
                                }}
                              />
                            ))}
                          </Box>
                        )}
                        {/* Περιοχή περιγραφής */}
                        <Box
                          sx={{
                            position: "relative",
                            minHeight: 76,
                            maxHeight: !expanded[idx] && isLong ? 120 : "none",
                            overflow: "hidden",
                            width: "100%",
                            mb: 1,
                            transition: "max-height 0.5s",
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontStyle="italic"
                            sx={{ wordBreak: "break-word", whiteSpace: "pre-line" }}
                          >
                            “{fullText}”
                          </Typography>
                          {!expanded[idx] && isLong && (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                width: "100%",
                                height: 40,
                                background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 85%)",
                                display: "flex",
                                alignItems: "flex-end",
                                justifyContent: "center",
                                pointerEvents: "none",
                              }}
                            />
                          )}
                        </Box>
                        {isLong && !expanded[idx] && (
                          <Button
                            onClick={() => handleExpand(idx)}
                            size="small"
                            sx={{ mb: 2 }}
                          >
                            {t("read_more") || "Διαβάστε περισσότερα"}
                          </Button>
                        )}

                        <Typography
                          variant="subtitle2"
                          color="primary"
                          fontWeight="bold"
                        >
                          {t(item.authorKey)}
                        </Typography>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>

        {/* About Teaser Section */}
        <Container sx={{ mt: 8 }}>
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <Card
              sx={{
                p: 4,
                background: "#fff",
                color: "#222",
                borderRadius: 4,
                maxWidth: 900,
                mx: "auto",
                boxShadow: 4,
                display: "flex",
                alignItems: "center",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              <Avatar
                alt="BrainstormVisions Logo"
                src="/logo.png"
                sx={{ width: 100, height: 100, mx: "auto", mb: { xs: 2, md: 0 }, mr: { md: 4 } }}
              />
              <Box flex={1}>
                <Typography variant="h5" fontWeight="bold" mb={1}>
                  {t("our_story")}
                </Typography>
                <Typography>{t("about_teaser")}</Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="medium"
                  component={Link}
                  to={makePath("/about")}
                  sx={{ mt: 2 }}
                >
                  {t("about_us")}
                </Button>
              </Box>
            </Card>
          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default Home;
